import * as effects from 'redux-saga/effects'
import { SagaIterator } from 'redux-saga'
import * as actions from './actions'
import * as api from './api'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import {
  CreateMarkerPropsType,
  EditMapPropsType,
  EditMarkerPropsType,
} from './types'

function* fetchMap(payload: { id: string }): SagaIterator {
  try {
    const map = yield effects.call(api.fetchMap, payload.id)
    yield effects.put(actions.fetchMapSuccess(map.data))
  } catch (error) {
    console.warn('Cannot fetch map', error)
    yield effects.put(actions.fetchMapFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchCourse(payload: { id: string }): SagaIterator {
  try {
    const course = yield effects.call(api.fetchCourse, payload.id)
    yield effects.put(actions.fetchCourseSuccess(course.data))
  } catch (error) {
    console.warn('Cannot fetch course', error)
    yield effects.put(actions.fetchCourseFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* editMap(payload: EditMapPropsType): SagaIterator {
  try {
    const { map_id, course_id, image, is_active } = payload
    yield effects.call(api.editMap, map_id, course_id, image, is_active)
    yield effects.put(actions.editMapSuccess())
    yield effects.put(actions.fetchMap(map_id))
  } catch (error) {
    console.warn('Cannot edit map', error)
    yield effects.put(actions.editMapFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createMarker(payload: CreateMarkerPropsType): SagaIterator {
  try {
    const { map_id, typeMarker, position_x, position_y, module_lesson_id } =
      payload
    yield effects.call(
      api.createMarker,
      map_id,
      typeMarker,
      position_x,
      position_y,
      module_lesson_id
    )
    yield effects.put(actions.createMarkerSuccess())
    yield effects.put(actions.fetchMap(map_id))
  } catch (error) {
    console.warn('Cannot create marker', error)
    yield effects.put(actions.createMarkerFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* editMarker(payload: EditMarkerPropsType): SagaIterator {
  try {
    const {
      map_id,
      marker_id,
      typeMarker,
      position_x,
      position_y,
      module_lesson_id,
    } = payload
    yield effects.call(
      api.editMarker,
      map_id,
      marker_id,
      typeMarker,
      position_x,
      position_y,
      module_lesson_id
    )
    yield effects.put(actions.editMarkerSuccess())
    yield effects.put(actions.fetchMap(map_id))
  } catch (error) {
    console.warn('Cannot edit marker', error)
    yield effects.put(actions.editMarkerFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* removeMarker(payload: EditMarkerPropsType): SagaIterator {
  try {
    const { map_id, marker_id } = payload
    yield effects.call(api.removeMarker, map_id, marker_id)
    yield effects.put(actions.removeMarkerSuccess())
    yield effects.put(actions.fetchMap(map_id))
  } catch (error) {
    console.warn('Cannot remove marker', error)
    yield effects.put(actions.removeMarkerFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* watchFetchMap(): SagaIterator {
  yield effects.takeEvery(actions.fetchMap.toString(), fetchMap)
}

function* watchFetchCourse(): SagaIterator {
  yield effects.takeEvery(actions.fetchCourse.toString(), fetchCourse)
}

function* watchEditMap(): SagaIterator {
  yield effects.takeEvery(actions.editMap.toString(), editMap)
}

function* watchCreateMarker(): SagaIterator {
  yield effects.takeEvery(actions.createMarker.toString(), createMarker)
}

function* watchEditMarker(): SagaIterator {
  yield effects.takeEvery(actions.editMarker.toString(), editMarker)
}

function* watchRemoveMarker(): SagaIterator {
  yield effects.takeEvery(actions.removeMarker.toString(), removeMarker)
}

export default function* watchAdminMapEdit(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchMap),
    effects.fork(watchEditMap),
    effects.fork(watchFetchCourse),
    effects.fork(watchCreateMarker),
    effects.fork(watchEditMarker),
    effects.fork(watchRemoveMarker),
  ])
}
